import React, { useState, useEffect } from 'react';
import { User, UserRole, SurveySection } from '../types';
import { getUsers, saveUser, updateUser, updateCurrentUserAuth, deleteUser, resetSurveys, getDynamicSections, repairSerialNumbers } from '../services/storage';
import { Trash2, UserPlus, Shield, AlertTriangle, Loader, Edit, Save, X, Database, Wrench } from 'lucide-react';
import { useToast } from './Toast';
import SecureActionModal from './SecureActionModal';

interface AdminManagerProps {
  currentUser: User;
}

const AdminManager: React.FC<AdminManagerProps> = ({ currentUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [sections, setSections] = useState<SurveySection[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showRules, setShowRules] = useState(false);
  
  // Form State
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    role: UserRole.REPRESENTATIVE,
    isActive: true,
    visibleSections: [] as string[]
  });
  
  const [error, setError] = useState('');
  const { addToast } = useToast();

  // Secure Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [actionType, setActionType] = useState<'DELETE_USER' | 'RESET_SYSTEM' | null>(null);
  const [targetId, setTargetId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [uData, sData] = await Promise.all([getUsers(), getDynamicSections()]);
      setUsers(uData);
      setSections(sData);
      setFormData(prev => ({ ...prev, visibleSections: sData.map(s => s.id) }));
    } catch (e) {
      addToast("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setIsEditing(true);
    setSelectedUserId(user.id);
    setFormData({
      name: user.name,
      username: user.username,
      password: '', // Blank by default
      role: user.role,
      isActive: user.isActive !== false,
      visibleSections: user.visibleSections || sections.map(s => s.id)
    });
    setError('');
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSelectedUserId(null);
    setFormData({ name: '', username: '', password: '', role: UserRole.REPRESENTATIVE, isActive: true, visibleSections: sections.map(s => s.id) });
    setError('');
  };

  const toggleSection = (id: string) => {
    const current = formData.visibleSections;
    if (current.includes(id)) {
      setFormData({...formData, visibleSections: current.filter(s => s !== id)});
    } else {
      setFormData({...formData, visibleSections: [...current, id]});
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name || (!isEditing && !formData.username) || (!isEditing && !formData.password)) {
      setError('Required fields are missing.');
      return;
    }

    setProcessing(true);
    try {
      if (isEditing && selectedUserId) {
        // Update Logic
        const isSelf = selectedUserId === currentUser.id;
        
        // 1. Update Profile Data in Firestore
        await updateUser(selectedUserId, {
          name: formData.name,
          role: formData.role,
          isActive: formData.isActive,
          visibleSections: formData.visibleSections
        });

        // 2. If it is SELF and password provided, update Auth
        if (isSelf && formData.password) {
           await updateCurrentUserAuth(formData.password);
           addToast('Profile and Password updated. Please login again if requested.', 'success');
        } else if (!isSelf && formData.password) {
           addToast('Note: Password was NOT changed. You cannot change other users passwords directly.', 'info');
        } else {
           addToast('User profile updated successfully', 'success');
        }

      } else {
        // Create Logic
        await saveUser({
          username: formData.username,
          name: formData.name,
          role: formData.role,
          visibleSections: formData.visibleSections
        }, formData.password);
        addToast('User created successfully', 'success');
      }

      handleCancelEdit();
      await loadData();
    } catch (err: any) {
      setError(err.message);
      addToast(err.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const initiateDeleteUser = (id: string, role: UserRole) => {
    if (role === UserRole.MASTER_ADMIN) {
      addToast('Cannot delete Master Admin', 'error');
      return;
    }
    setTargetId(id);
    setActionType('DELETE_USER');
    setModalOpen(true);
  };

  const initiateReset = () => {
    setActionType('RESET_SYSTEM');
    setModalOpen(true);
  }

  const handleConfirmAction = async () => {
    if (actionType === 'DELETE_USER' && targetId) {
      await deleteUser(targetId);
      addToast('User deleted successfully', 'info');
      await loadData();
    } else if (actionType === 'RESET_SYSTEM') {
      await resetSurveys();
      addToast('All survey data has been reset.', 'success');
    }
  };

  const handleRepairSerials = async () => {
     if(!confirm("This will scan ALL surveys for duplicate Serial Numbers. \n\nDuplicates will be assigned a new number at the end of the list. \n\nContinue?")) return;
     
     setProcessing(true);
     try {
       const msg = await repairSerialNumbers();
       addToast(msg, 'success');
       await loadData();
     } catch(e: any) {
       addToast(e.message, 'error');
     } finally {
       setProcessing(false);
     }
  }

  return (
    <div className="space-y-8 pb-20">
      {/* DB Rules Help Section */}
      <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg flex flex-col md:flex-row justify-between items-start md:items-center">
         <div>
            <h3 className="font-bold text-yellow-800 flex items-center"><Database size={16} className="mr-2"/> Firebase Persistence (30 Day Limit Fix)</h3>
            <p className="text-sm text-yellow-700 mt-1">If your database is in Test Mode, data may delete after 30 days. Click to see the permanent rules.</p>
         </div>
         <button 
           onClick={() => setShowRules(!showRules)} 
           className="mt-3 md:mt-0 text-sm bg-white border border-yellow-400 text-yellow-800 px-3 py-1.5 rounded hover:bg-yellow-100 font-bold"
         >
           {showRules ? 'Hide Rules' : 'View Rules'}
         </button>
      </div>

      {showRules && (
        <div className="bg-gray-800 text-gray-200 p-4 rounded-lg text-sm font-mono overflow-x-auto border border-gray-700">
          <p className="text-gray-400 mb-2">// Copy this into Firebase Console -&gt; Firestore Database -&gt; Rules</p>
          <pre>{`rules_version = '2';

service cloud.firestore {
  match /databases/{database}/documents {
    // Allow read/write access to all authenticated users
    match /{document=**} {
      allow read, write: if request.auth != null;
    }
  }
}`}</pre>
        </div>
      )}

      {/* Maintenance Zone */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-blue-800 flex items-center mb-2"><Wrench size={20} className="mr-2"/> System Maintenance</h3>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                  <p className="text-sm text-blue-700 font-bold">Repair Serial Numbers</p>
                  <p className="text-xs text-blue-600 mt-1">Scans for duplicate or missing serial numbers in old surveys and fixes them. Also syncs the global counter.</p>
              </div>
              <button 
                onClick={handleRepairSerials}
                disabled={processing}
                className="bg-blue-600 text-white px-4 py-2 rounded font-bold hover:bg-blue-700 shadow-sm text-sm whitespace-nowrap flex items-center"
              >
                {processing ? <Loader size={14} className="animate-spin mr-2"/> : null}
                {processing ? 'Scanning...' : 'Run Repair Tool'}
              </button>
          </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-emerald-600">
        <div className="flex justify-between items-center mb-6">
           <h2 className="text-xl font-bold text-gray-800 flex items-center">
             {isEditing ? <Edit className="mr-2"/> : <UserPlus className="mr-2"/>} 
             {isEditing ? 'Edit User' : 'Create New User'}
           </h2>
           {isEditing && (
             <button onClick={handleCancelEdit} className="text-gray-500 hover:text-red-500 flex items-center text-sm">
               <X size={16} className="mr-1"/> Cancel Editing
             </button>
           )}
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Full Name</label>
                <input 
                  className="border p-2.5 rounded w-full focus:ring-2 focus:ring-emerald-500 outline-none" 
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Username / Email
                  {isEditing && <span className="text-xs font-normal text-gray-400 ml-2">(Cannot change for others)</span>}
                </label>
                <input 
                  className={`border p-2.5 rounded w-full focus:ring-2 focus:ring-emerald-500 outline-none ${isEditing ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                  value={formData.username}
                  onChange={e => setFormData({...formData, username: e.target.value})}
                  placeholder="login_id"
                  disabled={isEditing}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  {isEditing ? 'Change Password' : 'Password'}
                  {isEditing && selectedUserId !== currentUser.id && <span className="text-xs font-normal text-red-400 ml-2">(Only user can change own password)</span>}
                </label>
                <input 
                  type="password"
                  className="border p-2.5 rounded w-full focus:ring-2 focus:ring-emerald-500 outline-none" 
                  value={formData.password}
                  onChange={e => setFormData({...formData, password: e.target.value})}
                  placeholder={isEditing ? "Leave blank to keep unchanged" : "********"}
                  disabled={isEditing && selectedUserId !== currentUser.id}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Role</label>
                <select 
                  className="border p-2.5 rounded w-full bg-white focus:ring-2 focus:ring-emerald-500 outline-none"
                  value={formData.role}
                  onChange={e => setFormData({...formData, role: e.target.value as UserRole})}
                >
                  <option value={UserRole.REPRESENTATIVE}>Representative</option>
                  <option value={UserRole.ADMIN}>Admin</option>
                  <option value={UserRole.MASTER_ADMIN}>Master Admin</option>
                </select>
              </div>

              {isEditing && selectedUserId !== currentUser.id && (
                <div className="flex items-center space-x-2 pt-6">
                  <label className="flex items-center cursor-pointer space-x-2">
                    <input 
                      type="checkbox" 
                      className="w-5 h-5 accent-emerald-600"
                      checked={formData.isActive}
                      onChange={e => setFormData({...formData, isActive: e.target.checked})}
                    />
                    <span className="font-bold text-gray-700">Account Active</span>
                  </label>
                </div>
              )}
           </div>

           {/* Permissions Section */}
           <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center"><Shield size={14} className="mr-2"/> Access Permissions (Admin Only)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                 {sections.map(section => (
                   <label key={section.id} className="flex items-center space-x-2 cursor-pointer">
                     <input 
                       type="checkbox" 
                       className="w-4 h-4 accent-emerald-600"
                       checked={formData.visibleSections.includes(section.id)}
                       onChange={() => toggleSection(section.id)}
                     />
                     <span className="text-sm text-gray-700">{section.label}</span>
                   </label>
                 ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">These settings control which parts of the dashboard/export are visible to this user (if Admin).</p>
           </div>

           {error && <div className="bg-red-50 text-red-600 p-3 rounded text-sm">{error}</div>}

           <div className="flex justify-end">
              <button type="submit" disabled={processing} className="bg-emerald-600 text-white px-6 py-2.5 rounded hover:bg-emerald-700 font-bold shadow flex items-center transition-colors">
                {processing ? <Loader className="animate-spin mr-2" size={18} /> : (isEditing ? <Save size={18} className="mr-2"/> : <UserPlus size={18} className="mr-2"/>)}
                {isEditing ? 'Update User' : 'Create User'}
              </button>
           </div>
        </form>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
          <h2 className="text-lg font-bold text-gray-700">All Users</h2>
          {loading && <Loader className="animate-spin text-gray-400" size={20} />}
        </div>
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map(u => (
              <tr key={u.id} className={u.id === currentUser.id ? 'bg-emerald-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {u.name} {u.id === currentUser.id && <span className="text-xs text-emerald-600 bg-emerald-100 px-1.5 rounded ml-2">YOU</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.username}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    u.role === UserRole.MASTER_ADMIN ? 'bg-purple-100 text-purple-800' :
                    u.role === UserRole.ADMIN ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    u.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {u.isActive !== false ? 'Active' : 'Deactivated'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex space-x-3">
                  <button 
                    onClick={() => handleEdit(u)}
                    className="text-blue-600 hover:text-blue-900 flex items-center"
                    title="Edit User"
                  >
                    <Edit size={16} className="mr-1"/> Edit
                  </button>
                  {u.role !== UserRole.MASTER_ADMIN && u.id !== currentUser.id && (
                    <button 
                      onClick={() => initiateDeleteUser(u.id, u.role)}
                      className="text-red-600 hover:text-red-900 flex items-center"
                      title="Delete User"
                    >
                      <Trash2 size={16} className="mr-1"/> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-6 mt-8">
        <h3 className="text-lg font-bold text-red-800 flex items-center mb-2"><AlertTriangle className="mr-2"/> Danger Zone</h3>
        <p className="text-sm text-red-600 mb-4">Resetting the system will permanently delete all collected survey data. User accounts and settings will be preserved.</p>
        <button 
          onClick={initiateReset}
          className="bg-red-600 text-white px-4 py-2 rounded font-bold hover:bg-red-700 shadow-sm"
        >
          Reset All Survey Data
        </button>
      </div>

      <SecureActionModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmAction}
        title={actionType === 'DELETE_USER' ? 'Delete User' : 'Reset System'}
        message={actionType === 'DELETE_USER' 
          ? "Are you sure you want to delete this user? This action requires your authorization."
          : "Are you sure you want to DELETE ALL SURVEY DATA? This cannot be undone."
        }
        actionButtonText={actionType === 'DELETE_USER' ? "Delete User" : "Reset Data"}
      />
    </div>
  );
};

export default AdminManager;